import {
    SortingData,
    TicketSortDirection,
    TicketSortField,
    TicketStatus,
    TicketVisibility,
    isTicketVisibility,
    PickedMention,
} from 'flow/types/Tickets';
import { List, Seq, Set as ImmutableSet } from 'immutable';
import { newestFirstSorter, oldestFirstSorter } from 'flow/utils/sorter-utils';

import { i18n } from 'i18next';
import { HTTPStatusCodes } from 'flow/utils';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { getContactName } from 'flow/utils/contact-utils';
import { Contact } from 'flow/types/Contact';
import {
    ConversationMessageKey,
    ConversationMessagePlaceholderKey,
    OMNI_CALL_EVENT_DTO_SUBTYPE,
    PREFIX_MESSAGE_PLACEHOLDER_KEY,
} from 'flow/types/TicketEventData';
import { Extension } from 'flow/types/Extension';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { RTCSession } from 'flow/types/RTCSession';
import { BagMessage } from 'flow/types/Top';
import { TypedMap } from 'flow/types/TypedMap';
import { atou } from './index';

const SIP_REQUEST_HEADER = Object.freeze({
    TICKET: 'X-Tvx-Omni-Ticket',
});
const TICKET_KEY_PREFIX = 'ticket-';

//These paths are used for storing ticket keys data used for respective tab in the tickets view
export const TICKET_KEYS_DATA_PATHS = Object.freeze({
    ASSIGNED_LOGGED_IN_USER: 'openTicketsAssignedToUserKeysData',
    UNASSIGNED: 'openTicketsUnassignedKeysData',
    CLOSED: 'closedTicketsKeysData',
    ALL_LIST: 'ticketsAllKeysData',
    RELATED: 'relatedKeysData',
});

export const TICKETS_LIST_TABS_KEYS = Object.freeze({
    MY_TICKETS: 'MY_TICKETS',
    UNASSIGNED: 'UNASSIGNED',
    ALL: 'ALL',
    // Temporary pill that hosts advanced-search results; only shown once the
    // user has applied a filter (see TicketsListsSection).
    FILTER: 'FILTER',
});

export const TICKETS_SETTINGS_KEYS = Object.freeze({
    AUTO_CREATION: {
        NEVER: 'NEVER',
        ALL_CALLS: 'ON_ALL_CALLS',
        MISSED_CALLS: 'ON_MISSED_CALLS',
    },
});

export const TICKETS_SETTINGS_PROPERTY_NAMES = Object.freeze({
    AUTO_CREATION: 'autoCreateTicket',
    AUTO_CLOSE_ON_MISSED_CALLS: 'closeOnMissedCall',
    AUTO_CLOSE_ON_HANGUP: 'closeOnHangUp',
});

// Keep in sync with the render branches in EventsListItemWrapperV2.tsx and the
// type guards in TicketEventData.ts. These are the only DTOSubtypes the client
// can render; anything else produces no UI.
export const SUPPORTED_TICKET_EVENT_DTO_SUBTYPES: ReadonlySet<string> = new Set(
    [
        // Call events (see eventIsCallRecordEvent)
        OMNI_CALL_EVENT_DTO_SUBTYPE,
        // Messages
        'OmniEmailMessageTicketEvent',
        'OmniInternalNoteTicketEvent',
        'OmniSmsNoteTicketEvent',
        'OmniWhatsAppMessageTicketEvent',
        'OmniWhatsAppUnsupportedTicketEvent', // despite the name, IS rendered (eventIsWhatsAppMessage)
        'OmniWhatsAppTemplateMessageTicketEvent',
        // Action events
        'OmniAssigneeChangedEvent',
        'OmniStateChangedEvent',
        'OmniSubjectChangedEvent',
    ],
);

export const eventIsSupportedForDisplay = (subtype?: string): boolean =>
    !!subtype && SUPPORTED_TICKET_EVENT_DTO_SUBTYPES.has(subtype);

// An unsupported event with text or attachments is a communication the user
// should know of, shown as a placeholder; one without is noise, never stored.
export const eventIsUnsupportedCommunication = (
    subtype: string | undefined,
    message: string | undefined,
    attachmentCount = 0,
): boolean =>
    !eventIsSupportedForDisplay(subtype) && (!!message || attachmentCount > 0);

export const eventIsKeptInStore = (
    subtype: string | undefined,
    message: string | undefined,
    attachmentCount = 0,
): boolean =>
    eventIsSupportedForDisplay(subtype) ||
    eventIsUnsupportedCommunication(subtype, message, attachmentCount);

/**
 * Comparator for the per-tab tickets keys data (SortingData), mirroring the
 * backend sorting exactly — the lists are paginated server-side, so client
 * and server must agree on the order or page merging breaks.
 *
 * 'lastUpdate' is the time of the latest meaningful update on the ticket;
 * the backend owns the definition of what counts.
 *
 * Ties on the chosen field break by createdDate, in the same direction as
 * the primary sort — mirroring the server's TICKET_ID tiebreaker (the unique
 * primary key, needed for stable pagination, also applied in the requested
 * direction). The client never receives that numeric id — the ticket key is
 * a UUID — so createdDate is the faithful proxy: ids are assigned in
 * creation order. The only residual divergence from the server is the rare
 * case of two tickets sharing both the primary value and createdDate.
 */
export const makeTicketsKeysDataComparator = (
    field: TicketSortField,
    direction: TicketSortDirection,
) => {
    const dateSorter =
        direction === 'asc' ? oldestFirstSorter : newestFirstSorter;

    return (data1: TypedMap<SortingData>, data2: TypedMap<SortingData>) => {
        const byField = dateSorter(data1.get(field), data2.get(field));
        if (byField !== 0) return byField;
        return dateSorter(data1.get('createdDate'), data2.get('createdDate'));
    };
};

export const getTicketIdFromTicketKey = (ticketKey: TicketKey) => {
    return ticketKey?.split(TICKET_KEY_PREFIX)[1] || '';
};

export const getTicketIdUiFriendlyFromTicketKey = (ticketKey: TicketKey) => {
    const ticketId = getTicketIdFromTicketKey(ticketKey);

    if (!ticketId) return '';
    if (isNaN(ticketId)) {
        return ticketId.slice(0, 5);
    } else {
        return `#${ticketId}`;
    }
};

export const getEntityKeyNextKey = (
    entries: Seq.Indexed<TicketKey>,
    entry: TicketKey,
) => {
    if (!entries) {
        return null;
    }
    const entryIndex = entries.indexOf(entry);

    if (entryIndex !== -1) {
        if (entryIndex === 0) {
            if (entryIndex + 1 < entries.size) {
                return entries.get(entryIndex + 1);
            } else if (entryIndex - 1 >= 0) {
                return entries.get(entryIndex - 1);
            } else {
                return null;
            }
        } else if (entryIndex === entries.size - 1) {
            return entries.get(entryIndex - 1);
        } else {
            return entries.get(entryIndex + 1);
        }
    }

    return null;
};

export const getTicketStatei18Key = (ticketState: TicketStatus) => {
    if (ticketState === 'open') {
        return 'pbx.open';
    }

    if (ticketState === 'closed') {
        return 'tickets.closed';
    }
    return ticketState;
};

export const getTicketIdUiFriendly = (key: TicketKey) => {
    const id = key?.split(TICKET_KEY_PREFIX)[1];
    if (!id) return '';
    return id.slice(0, 5)?.toUpperCase();
};

interface AssigneeOptionsProps {
    key: ExtensionKey | 'unassigned';
    label: string;
}

export const getTicketAssigneeOptionsFromMembers = (
    i18next: i18n,
    ownExtensionKey: ExtensionKey,
    // Ticket members arrive as a Set, queue members as a List; this only iterates.
    members?: ImmutableSet<ExtensionKey> | List<ExtensionKey>,
    extensions?: TypedMap<Record<ExtensionKey, TypedMap<Extension>>>,
) => {
    if (!members || !extensions) return [];

    const assigneeOptions: AssigneeOptionsProps[] = [];
    let ownExtensionOption;

    members.forEach((memberExtensionKey) => {
        if (memberExtensionKey === ownExtensionKey) {
            ownExtensionOption = {
                key: memberExtensionKey,
                label: i18next.t('chatlisting.me'),
            };
            return;
        }

        const memberContact = extensions.getIn([memberExtensionKey, 'contact']);
        if (!memberContact) return;

        const contactName =
            getContactName(memberContact) || i18next.t('Contact.Unknown');
        const option = { key: memberExtensionKey, label: contactName };
        assigneeOptions.push(option);
    });

    assigneeOptions.sort((a, b) => {
        //Sorting alphabetically
        if (a.label > b.label) {
            return 1;
        } else {
            return -1;
        }
    });

    if (ownExtensionOption) {
        //'Me' first in list
        assigneeOptions.unshift(ownExtensionOption);
    }

    //'Unassigned' last in list
    assigneeOptions.push({
        key: 'unassigned',
        label: i18next.t('tickets.unassigned.singular'),
    });

    return assigneeOptions;
};

export const rtcSessionRepresentsTicketNonVoice = (session: RTCSession) => {
    //return request?.hasHeader(SIP_REQUEST_HEADER.TICKET);

    try {
        return !!session._request.headers[SIP_REQUEST_HEADER.TICKET];
    } catch (_error) {
        return false;
    }
};

export const getTicketDataFromRtcSessionRequest = (session: RTCSession) => {
    if (!session || !session._request) {
        return undefined;
    }

    try {
        const headerValues =
            session._request.headers[SIP_REQUEST_HEADER.TICKET];
        const firstValueJson = atou(headerValues[0]['raw']);
        return JSON.parse(firstValueJson);
    } catch (_error) {
        return undefined;
    }
};

export const getDataFromBagInTopMessage = (
    // Partial: absent bag headers are handled by the VariableCount guard below.
    message: Partial<BagMessage>,
    bagName: 'telavox.omni.ticket' | 'telavox.omni.ticket_id',
) => {
    if (!message || !message.VariableCount) {
        return undefined;
    }

    try {
        const count = parseInt(message.VariableCount);
        for (let i = 0; i < count; i++) {
            const name = message[`VariableName${i}`];
            if (name !== bagName) {
                continue;
            }

            const type = message[`VariableType${i}`];
            const value = message[`VariableValue${i}`];

            if (type === 'JSON') {
                return JSON.parse(atou(value));
            }

            return atou(value);
        }

        return undefined;
    } catch (_error) {
        return undefined;
    }
};

export const getOmnichannelTypeName = (channelType: string, i18next: i18n) => {
    if (channelType === 'whatsapp') return 'WhatsApp';
    if (channelType === 'email') return i18next.t('option.email');
    return '';
};

/**
 * About function getCallerObjectComponentFriendly:
 * in the object returned there's displayString and formattingRequiresForcedLeftToRight.
 * The latter we set to true when it's only a phone number returned, i.e. when it looks
 * something like this: "011-234 56 78", and not "Jessica Doe (011-234 56 78)".
 * This is because pretty formatted numbers (containing a hyphen (-)) that are not paired with any words
 * will be formatted in an undesired way, where some of the digits are placed on the wrong side of the hyphen.
 *
 * We then explicitly set dir="ltr" in the component using displayString if formattingRequiresForcedLeftToRight is true
 * See return block in the component for more on this.
 */
export const getCallerObjectComponentFriendly = (
    contact: TypedMap<Contact>,
    callerNumber: string,
    nationalFormattingCountryCode: string,
    i18next: i18n,
) => {
    const callerNumberIsUnknown =
        callerNumber === 'unknown' || callerNumber === 'anonymous';
    const callerNumberPrettyFormatted = callerNumberIsUnknown
        ? i18next.t('call.hidden_number')
        : prettyFormatE164Number(callerNumber, nationalFormattingCountryCode);

    if (!contact)
        return {
            displayString: callerNumberPrettyFormatted,
            formattingRequiresForcedLeftToRight: !callerNumberIsUnknown,
        };

    const firstName = contact.get('firstName');
    const lastName = contact.get('lastName');

    if (firstName || lastName) {
        let nameString = '';
        if (firstName && lastName) {
            nameString = `${firstName} ${lastName}`;
        } else {
            nameString = firstName || lastName;
        }

        return {
            displayString: `${nameString} (${callerNumberPrettyFormatted})`,
            formattingRequiresForcedLeftToRight: false,
        };
    }

    const companyName = contact.get('companyName');
    if (companyName) {
        return {
            displayString: `${companyName} (${callerNumberPrettyFormatted})`,
            formattingRequiresForcedLeftToRight: false,
        };
    }

    return {
        displayString: callerNumberPrettyFormatted,
        formattingRequiresForcedLeftToRight: !callerNumberIsUnknown,
    };
};

export const keyIsTypeConversationMessage = (
    key?: string,
): key is ConversationMessageKey => key?.startsWith('conversationmessage-');

export const keyIsTypeConversationMessagePlaceholder = (
    key?: string,
): key is ConversationMessagePlaceholderKey =>
    key?.startsWith(PREFIX_MESSAGE_PLACEHOLDER_KEY);

/** The tier's own name. */
export const getTicketVisibilityNameI18Key = (visibility: TicketVisibility) => {
    switch (visibility) {
        case 'public':
            return 'tickets.access.visibility_public';
        case 'restricted':
            return 'tickets.access.visibility_restricted';
        case 'private':
            return 'tickets.access.visibility_private';
        default:
            return '';
    }
};

/**
 * What the tier grants an account that is not a member of the ticket. The same
 * keys the per-ticket general-access select uses, so both controls describe a
 * tier with the same words.
 */
export const getTicketVisibilityGrantI18Key = (
    visibility: TicketVisibility,
) => {
    switch (visibility) {
        case 'public':
            return 'tickets.access.general_access_option_public';
        case 'restricted':
            return 'tickets.access.general_access_option_restricted';
        case 'private':
            return 'tickets.access.general_access_option_private';
        default:
            return '';
    }
};

/**
 * The queue default arrives as the same lower-case tier the ticket DTO carries,
 * so no conversion is needed. What it can carry instead is client-server's
 * UNKNOWN fallback, which it deserializes any tier it cannot name to.
 *
 * Such a tier is dropped rather than guessed at. The store then holds the key
 * with no value, which Immutable's getIn reports as undefined instead of
 * reaching for its notSetValue, so the settings row names it as unknown. That
 * beats naming it private, which would state the queue grants less than it may.
 */
export const ticketVisibilityFromWire = (value: unknown) =>
    isTicketVisibility(value) ? value : undefined;

export const getQueueCreateTicketSettingsOptionI18Key = (option: string) => {
    switch (option) {
        case TICKETS_SETTINGS_KEYS.AUTO_CREATION.NEVER:
            return 'ticket.settings.queue.create.tickets.on.never';
        case TICKETS_SETTINGS_KEYS.AUTO_CREATION.ALL_CALLS:
            return 'ticket.settings.queue.create.tickets.on.allcalls';
        case TICKETS_SETTINGS_KEYS.AUTO_CREATION.MISSED_CALLS:
            return 'ticket.settings.queue.create.tickets.on.missedcalls';
        default:
            return '';
    }
};

/**
 * The rejection for a refused ticket request: an Error, so callers and tooling
 * can treat it as one, carrying the status that says why it was refused.
 */
export const ticketRequestError = (response?: { status?: number }) =>
    Object.assign(
        new Error(`Ticket request failed with status ${response?.status}`),
        { status: response?.status },
    );

/**
 * Whether the backend refused a ticket action because the viewer may not perform
 * it. Only a 403 says that: a refused write on a ticket the caller cannot see at
 * all answers 404, and every other status says nothing about access.
 */
export const ticketWriteWasRefused = (error?: { status?: number }) =>
    error?.status === HTTPStatusCodes.FORBIDDEN_403;

/**
 * Whether a failed ticket fetch means the viewer has no access to the ticket.
 *
 * A refused read is the only thing that does. omni-ticket-service answers 404
 * today, choosing not to reveal that a ticket it withholds exists at all, but
 * 403 is the other way to refuse and the backend may yet use it. Both are safe
 * to act on: a fetch passes the read gate alone, so neither can carry the write
 * gate's sense of 403, which is a ticket the caller may read but not write.
 *
 * Every other failure says nothing about access.
 */
export const ticketFetchErrorMeansNoAccess = (error?: Response) =>
    error?.status === HTTPStatusCodes.NOT_FOUND_404 ||
    error?.status === HTTPStatusCodes.FORBIDDEN_403;

/**
 * Moves the picked mentions with an edit of the note, so that each pick keeps
 * the index of its own "@". A pick that the edit touches is dropped.
 *
 * The caret after the edit locates it. The text alone cannot: when two
 * mentions read the same, deleting the first looks like deleting the second.
 */
export const shiftPickedMentions = (
    mentions: List<TypedMap<PickedMention>> | undefined,
    textBefore: string,
    textAfter: string,
    caretAfter: number,
) => {
    if (!mentions || mentions.isEmpty() || textBefore === textAfter) {
        return mentions;
    }

    const lengthChange = textAfter.length - textBefore.length;
    const editEnd = caretAfter - lengthChange;
    // A caret that does not sit at the end of the edit cannot locate it.
    if (
        editEnd < 0 ||
        editEnd > textBefore.length ||
        textBefore.slice(editEnd) !== textAfter.slice(caretAfter)
    ) {
        return List<TypedMap<PickedMention>>();
    }

    let editStart = 0;
    const editStartMax = Math.min(caretAfter, editEnd);
    while (
        editStart < editStartMax &&
        textBefore[editStart] === textAfter[editStart]
    ) {
        editStart++;
    }

    return mentions
        .filter((mention) => {
            const start = mention.get('start');
            const end = start + mention.get('name').length + 1;
            return end <= editStart || start >= editEnd;
        })
        .map((mention) =>
            mention.get('start') >= editEnd
                ? mention.set('start', mention.get('start') + lengthChange)
                : mention,
        )
        .toList();
};
